import { AppNavItem, AppNavLink, Css, NavLink, useTestIds } from "@homebound/beam";
import { camelCase } from "change-case";
import { ReactNode } from "react";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import { CoverImage, Icon } from "src/components";
import { BeamSideNavViewportLayout } from "src/components/layout/BeamSideNavViewportLayout";
import { useFeatureFlags } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, ProjectFeature, Stage } from "src/generated/graphqlTypes";
import { ProjectContextProps, useProjectContext } from "src/routes/projects/context/ProjectContext";
import {
  createBidPackagesUrl,
  createBudgetMetricsUrl,
  createBudgetUrl,
  createChangeEventsUrl,
  createCommitmentsUrl,
  createDocumentsUrl,
  createDraftScheduleUrl,
  createEstimatesUrl,
  createHomeownerContractsUrl,
  createInvoicesUrl,
  createPreConServicesUrl,
  createProductOfferingsUrl,
  createProjectBidContractsUrl,
  createProjectChangeLogUrl,
  createProjectDashboardUrl,
  createProjectDropsUrl,
  createProjectExpensesUrl,
  createProjectHomeownerNotesUrl,
  createProjectJobLogsUrl,
  createProjectLotSummaryUrl,
  createProjectScheduleUrl,
  createProjectSettingsUrl,
  createProjectToDosUrl,
  createSpecsAndSelectionsUrl,
} from "src/RouteUrls";
import * as projectPaths from "src/routing/domains/projectsPaths";
import { getProjectTypeIcon } from "src/utils/projects";

export function ProjectSideNavLayout() {
  const { sideNav, railWidthPx, contrastRail } = useProjectSideNav();

  return (
    <BeamSideNavViewportLayout sideNav={sideNav} railWidthPx={railWidthPx} contrastRail={contrastRail}>
      <Outlet />
    </BeamSideNavViewportLayout>
  );
}

export function useProjectSideNav() {
  const project = useProjectContext();
  const { pathname } = useLocation();
  const { featureIsEnabled } = useFeatureFlags();
  const { top, items, footerLinks, railWidthPx, contrastRail } = buildProjectSideNavProps(project, {
    pathname,
    featureIsEnabled,
  });

  return {
    sideNav: { top, items, footer: footerLinks.nonEmpty ? <ProjectSideNavFooter links={footerLinks} /> : undefined },
    railWidthPx,
    contrastRail,
  };
}

type BuildProjectSideNavOptions = { pathname: string; featureIsEnabled: (flag: FeatureFlagType) => boolean };

type ProjectNavContext = Pick<
  ProjectContextProps,
  | "id"
  | "__typename"
  | "name"
  | "features"
  | "latestActiveStage"
  | "hopUrl"
  | "canHaveBudget"
  | "clientContract"
  | "clientInvoice"
  | "shortClientNoun"
  | "buildAddress"
>;

type ProjectSideNavProps = {
  top?: ReactNode;
  items: AppNavItem[];
  footerLinks: AppNavLink[];
  railWidthPx: number;
  contrastRail: boolean;
};

export function buildProjectSideNavProps(
  project: ProjectNavContext,
  options: BuildProjectSideNavOptions,
): ProjectSideNavProps {
  const {
    id,
    __typename,
    name,
    features,
    latestActiveStage,
    hopUrl,
    canHaveBudget,
    clientContract,
    clientInvoice,
    shortClientNoun,
    buildAddress,
  } = project;

  if (!id || !__typename) {
    return { items: [], footerLinks: [], railWidthPx: 260, contrastRail: false };
  }

  if (__typename === "StandaloneProject") {
    const { items, footerLinks } = standaloneProjectNavItems(
      { shortClientNoun, clientContract, clientInvoice },
      id,
      latestActiveStage,
      features,
      hopUrl,
      options,
    );
    return {
      items,
      footerLinks,
      railWidthPx: 220,
      contrastRail: false,
      top: (
        <div css={Css.df.fdc.w100.$}>
          <CoverImage />
        </div>
      ),
    };
  }

  const { city, state, postalCode } = buildAddress ?? {};
  const { items, footerLinks } = hierarchyNavItems({ id, __typename, canHaveBudget, hopUrl }, options);
  return {
    items,
    footerLinks,
    railWidthPx: 260,
    contrastRail: false,
    top: (
      <div css={Css.df.fdc.ais.pt3.$}>
        <div css={Css.dif.aic.br4.bgGray200.gray600.hPx(40).pl1.pr2.gap1.mr4.$}>
          <Icon icon={getProjectTypeIcon({ __typename })} />
          <div css={Css.smSb.$}>{__typename}</div>
        </div>
        <div css={Css.df.fdc.pt1.$}>
          <div css={Css.lg.pr2.ptPx(5).pb1.lhPx(20).$}>{name}</div>
          {buildAddress && (
            <span css={Css.xs.$}>{`${city}, ${state} ${__typename !== "Community" ? postalCode : ""}`}</span>
          )}
        </div>
      </div>
    ),
  };
}

function ProjectSideNavFooter(props: { links: AppNavLink[] }) {
  const { links } = props;
  const tid = useTestIds({}, "appNav");

  return (
    <div css={Css.df.fdc.gap1.$}>
      {links.map((link) => (
        <NavLink
          key={link.label}
          variant="side"
          label={link.label}
          onClick={link.onClick}
          active={link.active}
          openInNew={link.openInNew}
          icon={link.icon}
          {...tid[`link_${camelCase(link.label)}`]}
        />
      ))}
    </div>
  );
}

type ProjectSideNavItems = { items: AppNavItem[]; footerLinks: AppNavLink[] };

function standaloneProjectNavItems(
  lotType: { shortClientNoun: string; clientContract: string; clientInvoice: string },
  projectId: string,
  stage: Stage,
  features: ProjectFeature[] = [],
  hopUrl: string,
  options: BuildProjectSideNavOptions,
): ProjectSideNavItems {
  const { pathname, featureIsEnabled } = options;
  const productConfigPlan = features.includes(ProjectFeature.ProductConfigPlan);

  const projectLinks: AppNavLink[] = [
    {
      label: "Dashboard",
      onClick: createProjectDashboardUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectDashboardRoute.path),
    },
    {
      label: "Schedule",
      onClick: productConfigPlan ? createDraftScheduleUrl(projectId) : createProjectScheduleUrl(projectId, stage),
      active: isNavActive(
        pathname,
        productConfigPlan ? projectPaths.projectDraftScheduleRoute.path : projectPaths.projectScheduleRoute.path,
      ),
    },
    {
      label: "Job Logs",
      onClick: createProjectJobLogsUrl(projectId),
      active: isNavActive(pathname, `${projectPaths.projectJobLogsRoute.path}/*`),
    },
    {
      label: "To-Dos",
      onClick: createProjectToDosUrl(projectId),
      active: isNavActive(pathname, `${projectPaths.projectToDosRoute.path}/*`),
    },
    ...(!productConfigPlan
      ? [
          {
            label: "Pre-Con Services",
            onClick: createPreConServicesUrl(projectId),
            active: isNavActive(pathname, projectPaths.projectPreConServicesRoute.path),
          },
        ]
      : []),
    {
      label: "Lot Summary",
      onClick: createProjectLotSummaryUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectLotSummaryRoute.path),
    },
    {
      label: "Specs & Selections",
      onClick: createSpecsAndSelectionsUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectSpecsAndSelectionsRoute.path),
    },
    {
      label: "Change Events",
      onClick: createChangeEventsUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectChangeEventsRoute.path),
    },
    ...(featureIsEnabled(FeatureFlagType.ChangeRequestMvp)
      ? [
          {
            label: "Change Log",
            onClick: createProjectChangeLogUrl(projectId),
            active: isNavActive(pathname, `${projectPaths.projectChangeLogRoute.path}/*`),
          },
        ]
      : []),
    {
      label: "Documents",
      onClick: createDocumentsUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectDocumentsRoute.path),
    },
    {
      label: "H/O Notes",
      onClick: createProjectHomeownerNotesUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectHomeownerNotesRoute.path),
    },
  ];

  const financeLinks: AppNavLink[] = [
    {
      label: "Budget",
      onClick: createBudgetUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectBudgetBaseRoute.path),
    },
    {
      label: "Estimates",
      onClick: createEstimatesUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectEstimatesRoute.path),
    },
    {
      label: lotType.clientContract.includes("Contract") ? "Contracts" : `${lotType.clientContract}s`,
      onClick: createHomeownerContractsUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectContractsRoute.path),
    },
    {
      label: `${lotType.clientInvoice}s`,
      onClick: createInvoicesUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectInvoicesRoute.path),
    },
    {
      label: "Trade Commitments",
      onClick: createCommitmentsUrl(projectId),
      active: isNavActive(pathname, [projectPaths.projectCommitmentsRoute.path, projectPaths.projectBillOptionalPath]),
    },
    {
      label: "Trade Expenses",
      onClick: createProjectExpensesUrl(projectId),
      active: isNavActive(pathname, projectPaths.projectExpensesRoute.path),
    },
  ];

  return {
    items: [
      { section: true, label: "Project", items: projectLinks },
      { section: true, label: "Finances", items: financeLinks },
      {
        section: true,
        items: [
          { label: "H/O Platform", onClick: hopUrl, active: false, openInNew: true, icon: "linkExternal" },
          {
            label: "Settings",
            onClick: createProjectSettingsUrl(projectId),
            active: isNavActive(pathname, projectPaths.projectSettingsRoute.path),
          },
        ],
      },
    ],
    footerLinks: [],
  };
}

type HierarchyProjectNavContext = Pick<ProjectContextProps, "id" | "__typename" | "canHaveBudget" | "hopUrl"> & {
  id: string;
  __typename: string;
};

function hierarchyNavItems(
  project: HierarchyProjectNavContext,
  options: BuildProjectSideNavOptions,
): ProjectSideNavItems {
  const { id, canHaveBudget, hopUrl, __typename } = project;
  const { pathname } = options;

  const dashboard: AppNavLink = {
    label: "Dashboard",
    onClick: createProjectDashboardUrl(id),
    active: isNavActive(pathname, projectPaths.projectDashboardRoute.path),
  };
  const schedule: AppNavLink = {
    label: "Schedule",
    onClick: createDraftScheduleUrl(id),
    active: isNavActive(pathname, projectPaths.projectDraftScheduleRoute.path),
  };
  const jobLogs: AppNavLink = {
    label: "Job Logs",
    onClick: createProjectJobLogsUrl(id),
    active: isNavActive(pathname, `${projectPaths.projectJobLogsRoute.path}/*`),
  };
  const budgetMetrics: AppNavLink = {
    label: "Metrics",
    onClick: createBudgetMetricsUrl(id),
    active: isNavActive(pathname, projectPaths.projectBudgetMetricsRoute.path),
  };
  const budget: AppNavLink = {
    label: "Budget",
    onClick: createBudgetUrl(id),
    active: isNavActive(pathname, projectPaths.projectBudgetBaseRoute.path),
  };
  const commitments: AppNavLink = {
    label: "POs",
    onClick: createCommitmentsUrl(id),
    active: isNavActive(pathname, [projectPaths.projectCommitmentsRoute.path, projectPaths.projectBillOptionalPath]),
  };
  const changeEvents: AppNavLink = {
    label: "Change Events",
    onClick: createChangeEventsUrl(id),
    active: isNavActive(pathname, projectPaths.projectChangeEventsRoute.path),
  };
  const expenses: AppNavLink = {
    label: "Expenses",
    onClick: createProjectExpensesUrl(id),
    active: isNavActive(pathname, projectPaths.projectExpensesRoute.path),
  };
  const estimates: AppNavLink = {
    label: "Estimates",
    onClick: createEstimatesUrl(id),
    active: isNavActive(pathname, projectPaths.projectEstimatesRoute.path),
  };
  const clientContracts: AppNavLink = {
    label: "Client Contracts",
    onClick: createHomeownerContractsUrl(id),
    active: isNavActive(pathname, projectPaths.projectContractsRoute.path),
  };
  const clientInvoices: AppNavLink = {
    label: "Client Invoices",
    onClick: createInvoicesUrl(id),
    active: isNavActive(pathname, projectPaths.projectInvoicesRoute.path),
  };
  const productOfferings: AppNavLink = {
    label: "Product Offerings",
    onClick: createProductOfferingsUrl(undefined, id),
    active: isNavActive(pathname, projectPaths.projectProductOfferingsRoute.path),
  };
  const drops: AppNavLink = {
    label: "Drops",
    onClick: createProjectDropsUrl(id),
    active: isNavActive(pathname, projectPaths.projectDropsRoute.path),
  };
  const bidPackages: AppNavLink = {
    label: "Bid Packages",
    onClick: createBidPackagesUrl(id),
    active: isNavActive(pathname, [
      projectPaths.projectBidPackagesRoute.path,
      projectPaths.projectBidCoverageRoute.path,
    ]),
  };
  const bidContracts: AppNavLink = {
    label: "Bid Contracts",
    onClick: createProjectBidContractsUrl(id),
    active: isNavActive(pathname, projectPaths.projectBidContractsRoute.path),
  };
  const scope: AppNavLink = {
    label: "Scope",
    onClick: createSpecsAndSelectionsUrl(id),
    active: isNavActive(pathname, projectPaths.projectSpecsAndSelectionsRoute.path),
  };
  const lotSummary: AppNavLink = {
    label: "Lot Summary",
    onClick: createProjectLotSummaryUrl(id),
    active: isNavActive(pathname, projectPaths.projectLotSummaryRoute.path),
  };
  const changeLog: AppNavLink = {
    label: "Change Log",
    onClick: createProjectChangeLogUrl(id),
    active: isNavActive(pathname, `${projectPaths.projectChangeLogRoute.path}/*`),
  };
  const documents: AppNavLink = {
    label: "Documents",
    onClick: createDocumentsUrl(id),
    active: isNavActive(pathname, projectPaths.projectDocumentsRoute.path),
  };
  const settings: AppNavLink = {
    label: "Settings",
    onClick: createProjectSettingsUrl(id),
    active: isNavActive(pathname, projectPaths.projectSettingsRoute.path),
  };
  const communityBudgets = [budgetMetrics, budget, commitments, changeEvents, expenses, estimates, clientContracts];
  const fullBudgets = [...communityBudgets, clientInvoices];
  const scheduleGroup = {
    label: "Schedule",
    items: [schedule, jobLogs],
    defaultExpanded: [schedule, jobLogs].some((item) => item.active),
  };
  const fullBudgetsGroup = {
    label: "Budgets",
    items: fullBudgets,
    defaultExpanded: fullBudgets.some((item) => item.active),
  };
  const bidsGroup = {
    label: "Bids",
    items: [productOfferings, drops, bidPackages, bidContracts],
    defaultExpanded: [productOfferings, drops, bidPackages, bidContracts].some((item) => item.active),
  };

  let items: AppNavItem[];
  switch (__typename) {
    case "Region":
      items = [dashboard, documents];
      break;
    case "Market": {
      const marketBids = [bidPackages, bidContracts];
      items = [
        dashboard,
        { label: "Bids", items: marketBids, defaultExpanded: marketBids.some((item) => item.active) },
        documents,
      ];
      break;
    }
    case "Community":
    case "Subdivision":
      items = [
        dashboard,
        schedule,
        !canHaveBudget
          ? {
              label: "Budgets",
              onClick: createBudgetMetricsUrl(id),
              active: isNavActive(pathname, projectPaths.projectBudgetMetricsRoute.path),
            }
          : {
              label: "Budgets",
              items: communityBudgets,
              defaultExpanded: communityBudgets.some((item) => item.active),
            },
        bidsGroup,
        ...(canHaveBudget ? [scope] : []),
        documents,
      ];
      break;
    case "Lot":
      items = [dashboard, scheduleGroup, fullBudgetsGroup, scope, changeLog, documents];
      break;
    case "Structure":
    case "Unit": {
      const homeownerLinks: AppNavLink[] = [
        {
          label: "H/O Notes",
          onClick: createProjectHomeownerNotesUrl(id),
          active: isNavActive(pathname, projectPaths.projectHomeownerNotesRoute.path),
        },
        { label: "H/O Platform", onClick: hopUrl, active: false, openInNew: true, icon: "linkExternal" },
      ];
      items = [
        dashboard,
        scheduleGroup,
        fullBudgetsGroup,
        scope,
        lotSummary,
        changeLog,
        documents,
        { label: "Homeowner", items: homeownerLinks, defaultExpanded: homeownerLinks.some((item) => item.active) },
      ];
      break;
    }
    default:
      throw new Error(`Unexpected hierarchy project type: ${__typename}`);
  }

  return { items, footerLinks: [settings] };
}

function isNavActive(pathname: string, path?: string | string[]): boolean {
  if (!path) return false;
  const paths = Array.isArray(path) ? path : [path];
  return paths.some((p) => !!matchPath({ path: p, end: false }, pathname));
}
